// AIArchitectApplyLab.jsx
// Reworked AI Architect frontend:
// Describe -> Clarify -> Design -> Approve -> Blueprint -> Tables -> BRs -> Jobs -> ACLs -> Assemble -> Test Scripts -> Preview -> Apply
//
// Main improvements:
// - Handles "needs_clarification" responses from the backend.
// - Shows architect questions clearly instead of treating them as errors.
// - Keeps the user in the correct step when backend asks for more info.
// - Better displays warnings, skipped builders, empty business rules, empty jobs, validator errors.
// - Separates chat from technical workspace.
// - Keeps modular builder flow safe: tables, BRs, jobs, ACLs are isolated.
// - Works with the rewritten ai_architect backend endpoints.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FlowDiagram from "../components/ai-architect/FlowDiagram";
import { normalizePlanState } from "../components/ai-architect/buildFlowGraph";
import {
  Sparkles,
  Send,
  CheckCircle2,
  PackageCheck,
  Eye,
  PlayCircle,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  AlertTriangle,
  ChevronRight,
  Bot,
  User,
  FileJson,
  ListChecks,
  ClipboardCheck,
  Layers3,
  MessageSquareText,
  Code2,
  Table2,
  Shield,
  Clock3,
  GitBranch,
  Hammer,
  Workflow,
  HelpCircle,
  Info,
  Check,
  XCircle,
  CircleDashed,
  SearchCheck,
  Braces,
  History,
  ArrowUpRight,
  Undo2,
  Users,
  MousePointerClick,
  Sparkle,
  ShieldAlert,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  `${window.location.protocol}//${window.location.hostname}:8000`;

const DEFAULT_PROMPT =
  "Create an app called AI Financial News Signal Digest. Use scheduled jobs only. Do not create any business rules. At 07:00 every day fetch financial news from the last 24 hours, store articles, analyze stock signals with AI, and store the analysis. At 08:00 send a clean email digest to popovstefan1993@gmail.com using SMTP properties from get_prop('smtp.*'). Clean up articles older than 48 hours.";

const STEPS = [
  { key: "describe", title: "Describe", label: "1", icon: MessageSquareText },
  { key: "clarify", title: "Clarify", label: "2", icon: HelpCircle },
  { key: "design", title: "Design", label: "3", icon: SearchCheck },
  { key: "approve", title: "Approve", label: "4", icon: CheckCircle2 },
  { key: "blueprint", title: "Blueprint", label: "5", icon: Workflow },
  { key: "tables", title: "Tables", label: "6", icon: Table2 },
  { key: "business_rules", title: "BRs", label: "7", icon: GitBranch },
  { key: "scheduled_jobs", title: "Jobs", label: "8", icon: Clock3 },
  { key: "roles", title: "Roles", label: "9", icon: Users },
  { key: "acls", title: "ACLs", label: "10", icon: Shield },
  { key: "ui_actions", title: "Actions", label: "11", icon: MousePointerClick },
  { key: "assemble", title: "Assemble", label: "12", icon: Hammer },
  { key: "script_test", title: "Test Scripts", label: "13", icon: Code2 },
  { key: "preview", title: "Preview", label: "14", icon: Eye },
  { key: "apply", title: "Apply", label: "15", icon: PlayCircle },
];

const STEP_ORDER = STEPS.map((s) => s.key);

// Friendly-mode overrides for the "Recommended next step" card — same
// underlying step machine, warmer plain-English copy instead of developer
// terms. Any step not listed here just keeps whatever nextAction already
// computed (e.g. "apply"/"preview" already read fine either way).
const FRIENDLY_STEP_COPY = {
  approve: {
    title: "Does this sound right?",
    description: "Take a look at the plan above. If anything's off, just tell me in the chat.",
    button: "Yes, keep going",
  },
  blueprint: {
    title: "Let me put together a plan",
    description: "I'll figure out everything this needs — the pieces, not the details yet.",
    button: "Build the plan",
  },
  preview: {
    title: "One last look before I build this for real",
    description: "Nothing has been built yet — this just shows exactly what will happen.",
    button: "Show me what will happen",
  },
  apply: {
    title: "All set!",
    description: "This is ready. When you're happy with it, start a new request any time.",
    button: "Start something new",
  },
};

// Everything from "tables" through "script_test" is internal build
// machinery a non-technical user shouldn't have to click through as
// separate, differently-labeled steps. Friendly mode shows one persistent
// "Building your application…" card for all of them — same underlying
// step machine and button/action underneath, only the title/description/
// icon are unified. "preview" and "apply" stay as their own distinct,
// meaningful checkpoints (see FRIENDLY_STEP_COPY above), since those are
// real moments worth a deliberate confirmation, not just internal steps.
const FRIENDLY_BUILDING_SUBSTATUS = {
  tables: "Setting up your data",
  business_rules: "Setting up automatic behavior",
  scheduled_jobs: "Setting up anything time-based",
  roles: "Setting up roles",
  acls: "Setting up permissions",
  ui_actions: "Wiring up buttons",
  assemble: "Putting it all together",
  script_test: "Double-checking everything works",
};
const FRIENDLY_BUILDING_STEPS = Object.keys(FRIENDLY_BUILDING_SUBSTATUS);

function getDisplayedStepCopy(uiMode, currentStep, fallback) {
  if (uiMode !== "friendly") return fallback;

  if (FRIENDLY_BUILDING_STEPS.includes(currentStep)) {
    return {
      ...fallback,
      title: "Building your application…",
      description: FRIENDLY_BUILDING_SUBSTATUS[currentStep],
      icon: Hammer,
    };
  }

  const friendly = FRIENDLY_STEP_COPY[currentStep];
  if (!friendly) return fallback;
  return { ...fallback, ...friendly };
}

const STATUS_TO_STEP = {
  needs_clarification: "clarify",
  business_rules_generation_failed: "tables",
  scheduled_jobs_generation_failed: "business_rules",
  in_discovery: "clarify",
  in_design: "design",
  design_ready: "design",
  design_approved: "approve",
  blueprint_generated: "blueprint",
  tables_fields_generated: "tables",
  business_rules_generated: "business_rules",
  scheduled_jobs_generated: "scheduled_jobs",
  roles_generated: "roles",
  acls_generated: "acls",
  ui_actions_generated: "ui_actions",
  assembled_package_generated: "assemble",
  script_test_passed: "script_test",
  script_test_failed: "script_test",
  script_test_completed_with_errors: "script_test",
  preview_ready: "preview",
  preview_only: "preview",
  package_applied: "apply",
};

function getAuthToken() {
  return localStorage.getItem("token");
}

function isClarificationResponse(data) {
  if (!data) return false;
  if (data.status === "needs_clarification") return true;
  if (Array.isArray(data.questions) && data.questions.length > 0) return true;
  if (Array.isArray(data.discovery_questions) && data.discovery_questions.length > 0) {
    return true;
  }
  return false;
}

function getQuestions(data) {
  if (!data) return [];
  if (Array.isArray(data.questions)) return data.questions;
  if (Array.isArray(data.discovery_questions)) return data.discovery_questions;
  if (Array.isArray(data?.blueprint?.discovery_questions)) {
    return data.blueprint.discovery_questions;
  }
  return [];
}

function getWarnings(data) {
  if (!data) return [];

  const candidates = [
    data.warnings,
    data?.blueprint?.warnings,
    data?.tables_fields?.warnings,
    data?.business_rules?.warnings,
    data?.scheduled_jobs?.warnings,
    data?.acls?.warnings,
    data?.business_rules_build?.warnings,
    data?.scheduled_jobs_build?.warnings,
    data?.change_package?.warnings,
    data?.assembled_blueprint?.warnings,
  ];

  return candidates
    .filter(Array.isArray)
    .flat()
    .filter(Boolean);
}

function getRepairAttempts(data) {
  if (!data) return [];

  const candidates = [
    data.repair_attempts,
    data?.business_rules?.repair_attempts,
    data?.scheduled_jobs?.repair_attempts,
    data?.business_rules_build?.repair_attempts,
    data?.scheduled_jobs_build?.repair_attempts,
  ];

  const found = candidates.find(Array.isArray);
  return found || [];
}

function getValidationError(data) {
  if (!data) return "";

  return (
    data.validation_error ||
    data?.business_rules?.validation_error ||
    data?.scheduled_jobs?.validation_error ||
    data?.business_rules?.warnings?.[0]?.message ||
    data?.scheduled_jobs?.warnings?.[0]?.message ||
    ""
  );
}

function isGenerationFailed(data) {
  return (
    data?.status === "generation_failed" ||
    data?.status === "business_rules_generation_failed" ||
    data?.status === "scheduled_jobs_generation_failed"
  );
}


function isScriptTestResult(data) {
  return [
    "script_test_passed",
    "script_test_failed",
    "script_test_completed_with_errors",
  ].includes(data?.status);
}

function getScriptTestReadableText(data) {
  const summary = data?.summary || {};
  const failed = data?.failed_script;

  if (data?.status === "script_test_passed") {
    return [
      data.message || "All generated scripts passed in dry-run mode.",
      "",
      `Scripts tested: ${summary.tested ?? summary.total_scripts ?? 0}`,
      `Passed: ${summary.passed ?? 0}`,
      `Failed: ${summary.failed ?? 0}`,
    ].join("\n");
  }

  if (failed) {
    const aiAnalysis = failed.ai_analysis;
    const diagnosis =
      aiAnalysis?.diagnosis ||
      aiAnalysis?.root_cause ||
      aiAnalysis?.explanation ||
      "";

    return [
      data.message || "Script test stopped on the first runtime error.",
      "",
      `Failed script: ${failed.name || "Unnamed script"}`,
      `Type: ${failed.type || "script"}`,
      `Order: ${failed.order ?? ""}`,
      "",
      failed.traceback ? `Traceback:\n${failed.traceback}` : "",
      diagnosis ? `\nAI analysis:\n${diagnosis}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }

  return data.message || `Status: ${data.status}`;
}

function getReadableText(data) {
  if (!data) return "";

  if (data.error) return String(data.error);

  if (isScriptTestResult(data)) {
    return getScriptTestReadableText(data);
  }

  if (isGenerationFailed(data)) {
    const attempts = getRepairAttempts(data);
    const validationError = getValidationError(data);

    return [
      data.assistant_message ||
        data.message ||
        "AI Architect could not safely generate this script after automatic repair attempts.",
      validationError ? `Validation error: ${validationError}` : "",
      attempts.length ? `Automatic repair attempts: ${attempts.length}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");
  }

  const questions = getQuestions(data);
  if (questions.length > 0) {
    return [
      data.message || "I need a few details before I can safely continue.",
      "",
      ...questions.map((q, i) => `${i + 1}. ${typeof q === "string" ? q : q.question || JSON.stringify(q)}`),
    ].join("\n");
  }

  if (data.assistant_message) return data.assistant_message;
  if (data.documentation) return data.documentation;
  if (data.implementation_package) return data.implementation_package;
  if (data.message) return data.message;

  if (data.status) {
    const warnings = getWarnings(data);
    if (warnings.length > 0) {
      return [
        `Status: ${data.status}`,
        "",
        "Warnings:",
        ...warnings.map((w, i) => `${i + 1}. ${typeof w === "string" ? w : w.message || w.reason || JSON.stringify(w)}`),
      ].join("\n");
    }

    return `Status: ${data.status}`;
  }

  return "Done.";
}

function classifyResultType(data, fallback = "normal") {
  if (!data) return fallback;
  if (data.error) return "error";
  if (data.status === "script_test_failed") return "error";
  if (data.status === "script_test_completed_with_errors") return "warning";
  if (data.status === "script_test_passed") return "success";
  if (isGenerationFailed(data)) return "error";
  if (isClarificationResponse(data)) return "clarification";
  if (getWarnings(data).length > 0) return "warning";
  if (data.status === "apply_blocked") return "error";
  if (data.status === "package_applied") {
    return data.needs_review_count > 0 ? "warning" : "success";
  }
  return fallback;
}

function getNextStepFromData(data, fallbackStep) {
  if (!data || data.error) return fallbackStep;

  if (isGenerationFailed(data)) {
    if (data.business_rules) return "tables";
    if (data.scheduled_jobs) return "business_rules";
    if (data.ui_actions) return "acls";
    return fallbackStep;
  }

  if (isClarificationResponse(data)) return "clarify";
  if (data.status && STATUS_TO_STEP[data.status]) return STATUS_TO_STEP[data.status];
  return fallbackStep;
}

function countArray(value) {
  return Array.isArray(value) ? value.length : 0;
}

function safeJson(data) {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

export default function AIArchitectApplyLab({ embedded = false }) {
  const { sessionId: sessionIdParam } = useParams();
  const navigate = useNavigate();

  const [sessionId, setSessionId] = useState("");
  const [input, setInput] = useState(DEFAULT_PROMPT);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [workspaceHistory, setWorkspaceHistory] = useState([]);
  const [currentStep, setCurrentStep] = useState("describe");
  const [error, setError] = useState("");
  const [autoAdvance, setAutoAdvance] = useState(true);

  const [recentSessions, setRecentSessions] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [resuming, setResuming] = useState(false);

  const [rollbackResult, setRollbackResult] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackError, setRollbackError] = useState("");
  const [forceIds, setForceIds] = useState([]);

  // Friendly (default): plain-language, flow-diagram-first experience for
  // non-technical users. Technical: today's Builder Workspace tabs, for
  // reviewing business rule/UI action scripts, ACLs, and raw JSON.
  const [uiMode, setUiMode] = useState("friendly");

  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Resume from the URL: /ai-architect-lab/:sessionId lands here on a fresh
  // page load (refresh, shared link, or clicking a "Recent Sessions" entry)
  // and reconstructs enough state to keep working, not just to look at.
  useEffect(() => {
    if (!sessionIdParam || sessionIdParam === sessionId) return;

    let cancelled = false;
    setResuming(true);
    setError("");

    (async () => {
      try {
        const data = await fetchJson(`/ai-architect/sessions/${sessionIdParam}`);
        if (cancelled) return;

        const { session, messages: dbMessages, latest_design, latest_package } = data;

        setSessionId(session.id);
        setMessages(
          (dbMessages || []).map((m) => ({
            role: m.role,
            title: m.role === "user" ? "You" : "AI Architect",
            content: m.content,
            type: "normal",
            createdAt: m.created_at
              ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
              : "",
          }))
        );

        const resumedResult = latest_package?.package_json || latest_design?.design_json || null;
        setLastResult(resumedResult);
        setCurrentStep(STATUS_TO_STEP[session.status] || "describe");
      } catch (e) {
        if (!cancelled) {
          setError(`Could not resume session: ${e.message || "unknown error"}`);
          navigate("/ai-architect-lab", { replace: true });
        }
      } finally {
        if (!cancelled) setResuming(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdParam]);

  // Keep the URL in sync once a session exists, so refreshing or sharing the
  // link resumes the same session instead of landing on a blank one. Uses
  // `replace` so New Session / back-navigation doesn't pile up history entries.
  useEffect(() => {
    if (sessionId && sessionId !== sessionIdParam) {
      navigate(`/ai-architect-lab/${sessionId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Recent-sessions list for the empty state, so a lost/closed session is
  // actually recoverable through the UI instead of only in theory via the DB.
  useEffect(() => {
    if (sessionIdParam || sessionId) return;

    let cancelled = false;
    setLoadingRecent(true);

    fetchJson("/ai-architect/sessions/?limit=8")
      .then((data) => {
        if (!cancelled) setRecentSessions(data.sessions || []);
      })
      .catch(() => {
        if (!cancelled) setRecentSessions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRecent(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdParam, sessionId]);

  const activeStepIndex = Math.max(
    0,
    STEPS.findIndex((s) => s.key === currentStep)
  );

  const nextAction = useMemo(() => {
    if (!sessionId) {
      return {
        title: "Start with your app idea",
        description:
          "Describe the app/change. The architect will first check if it has enough information and ask questions if needed.",
        button: "Send Request",
        icon: Send,
      };
    }

    if (currentStep === "clarify") {
      return {
        title: "Answer the architect questions",
        description:
          "The backend needs more details before it can safely generate design or code. Reply in the chat box.",
        button: "Send Answers",
        icon: HelpCircle,
      };
    }

    if (currentStep === "design") {
      return {
        title: "Review the proposed design",
        description:
          "Make sure the solution is correct. If something is wrong, reply with corrections before approving.",
        button: "Approve Design",
        icon: CheckCircle2,
      };
    }

    if (currentStep === "approve") {
      return {
        title: "Generate modular blueprint",
        description:
          "Create the implementation plan: tables, fields, business rules, scheduled jobs, ACLs, UI, warnings, and build order.",
        button: "Generate Blueprint",
        icon: Workflow,
      };
    }

    if (currentStep === "blueprint") {
      return {
        title: "Build tables and fields",
        description:
          "Generate only dynamic table and field metadata. No automations or ACLs in this step.",
        button: "Build Tables",
        icon: Table2,
      };
    }

    if (currentStep === "tables") {
      return {
        title: "Build business rules",
        description:
          "Generate only record-triggered business rules. If no business rules are needed, the backend should return an empty list with a warning.",
        button: "Build BRs",
        icon: GitBranch,
      };
    }

    if (currentStep === "business_rules") {
      return {
        title: "Build scheduled jobs",
        description:
          "Generate only time-based jobs. Scripts must be complete executable Python and must not use fake helper functions.",
        button: "Build Jobs",
        icon: Clock3,
      };
    }

    if (currentStep === "scheduled_jobs") {
      return {
        title: "Check for any new roles needed",
        description:
          "Propose a brand-new role only if this request genuinely needs one that doesn't already exist.",
        button: "Build Roles",
        icon: Users,
      };
    }

    if (currentStep === "roles") {
      return {
        title: "Build ACLs",
        description:
          "Generate access control metadata based on existing and newly-proposed roles and tables.",
        button: "Build ACLs",
        icon: Shield,
      };
    }

    if (currentStep === "acls") {
      return {
        title: "Build any button actions",
        description:
          "Generate real, clickable UI action buttons (e.g. \"Send Email\") only if the request describes one.",
        button: "Build Actions",
        icon: MousePointerClick,
      };
    }

    if (currentStep === "ui_actions") {
      return {
        title: "Assemble final package",
        description:
          "Combine all modular outputs into one executable change package.",
        button: "Assemble Package",
        icon: Hammer,
      };
    }

    if (currentStep === "assemble") {
      return {
        title: "Test generated scripts",
        description:
          "Run generated scheduled job and business rule scripts in order. If one fails, capture the traceback and ask AI to analyze the exact error.",
        button: "Test Scripts",
        icon: Code2,
      };
    }

    if (currentStep === "script_test") {
      if (lastResult?.status === "script_test_passed") {
        return {
          title: "Scripts passed",
          description:
            "All generated scripts ran successfully in dry-run mode. Now preview the package before applying it.",
          button: "Preview Apply",
          icon: Eye,
        };
      }

      return {
        title: "Script test found an error",
        description:
          "Review the traceback and AI analysis in the Tests tab. You can send a correction request in the chat or inspect the raw response.",
        button: "Send Fix Request",
        icon: AlertTriangle,
      };
    }

    if (currentStep === "preview") {
      return {
        title: "Apply metadata changes",
        description:
          "Only apply after reviewing the preview. This writes supported metadata changes.",
        button: "Apply Package",
        icon: PlayCircle,
      };
    }

    if (currentStep === "apply") {
      return {
        title: "Package applied",
        description:
          "Review applied/skipped records, then start a new session for the next app/change.",
        button: "New Session",
        icon: RotateCcw,
      };
    }

    return {
      title: "Continue the conversation",
      description: "Send feedback, corrections, or extra requirements.",
      button: "Send",
      icon: Send,
    };
  }, [sessionId, currentStep, lastResult]);

  const callApi = async (path, body = {}) => {
    setLoading(true);
    setError("");

    try {
      const token = getAuthToken();

      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });

      let data = null;

      try {
        data = await res.json();
      } catch {
        data = { message: await res.text() };
      }

      if (!res.ok) {
        const errorData = {
          error: data?.detail || data?.message || "Request failed",
          raw: data,
          status_code: res.status,
        };

        setLastResult(errorData);
        setError(errorData.error);
        return errorData;
      }

      setLastResult(data);

      setWorkspaceHistory((prev) => [
        {
          step: currentStep,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          data,
        },
        ...prev,
      ]);

      if (data.session_id) setSessionId(data.session_id);

      return data;
    } catch (e) {
      const errorData = { error: e.message || "Network error" };
      setLastResult(errorData);
      setError(errorData.error);
      return errorData;
    } finally {
      setLoading(false);
    }
  };

  // Plain GET, deliberately separate from callApi: session listing/detail
  // reads shouldn't push onto workspaceHistory or flip the global loading
  // spinner the same way a builder-step POST does.
  const fetchJson = async (path) => {
    const token = getAuthToken();
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    return res.json();
  };

  // Plain POST, deliberately separate from callApi: rollback is a post-apply
  // side action, not a step in the build wizard, so it must never touch
  // lastResult/currentStep/workspaceHistory — doing so would confuse the
  // step machine that drives the rest of this page.
  const postJson = async (path, body) => {
    const token = getAuthToken();
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    let data = null;
    try {
      data = await res.json();
    } catch {
      data = { message: await res.text() };
    }
    if (!res.ok) {
      throw new Error(data?.detail || data?.message || `Request failed (${res.status})`);
    }
    return data;
  };

  const addMessage = ({ role, title, content, type = "normal" }) => {
    setMessages((prev) => [
      ...prev,
      {
        role,
        title,
        content,
        type,
        createdAt: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
  };

  const addAssistantResult = (data, title = "AI Architect", type = "normal") => {
    addMessage({
      role: "assistant",
      title,
      content: getReadableText(data),
      type: classifyResultType(data, type),
    });
  };

  const updateStepFromResult = (data, fallback) => {
    if (!autoAdvance || data?.error) return;

    const next = getNextStepFromData(data, fallback);
    setCurrentStep(next);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const textValue = input.trim();

    addMessage({
      role: "user",
      title: sessionId ? "You" : "Initial Request",
      content: textValue,
    });

    setInput("");

    if (!sessionId) {
      const data = await callApi("/ai-architect/sessions/start", {
        app_name: "stay_interviews",
        request_text: textValue,
        limit: 30,
      });

      addAssistantResult(data, isClarificationResponse(data) ? "Clarification Needed" : "Proposed Design", "design");
      updateStepFromResult(data, "design");
      return;
    }

    const data = await callApi(`/ai-architect/sessions/${sessionId}/message`, {
      message: textValue,
      limit: 30,
    });

    addAssistantResult(data, isClarificationResponse(data) ? "More Info Needed" : "AI Architect");

    if (!data?.error) {
      if (isClarificationResponse(data)) {
        setCurrentStep("clarify");
      } else if (currentStep === "clarify") {
        setCurrentStep("design");
      } else {
        updateStepFromResult(data, currentStep);
      }
    }
  };

  const approveDesign = async () => {
    if (!sessionId) return;

    const data = await callApi(
      `/ai-architect/sessions/${sessionId}/approve-design`,
      { approved: true }
    );

    addAssistantResult(data, "Design Approved", "success");
    updateStepFromResult(data, "approve");
  };

  const generateBlueprint = async () => {
    const data = await callApi(`/ai-architect/sessions/${sessionId}/blueprint`, {
      limit: 30,
    });

    addAssistantResult(data, "Blueprint Ready", "blueprint");
    updateStepFromResult(data, "blueprint");
  };

  const buildTablesFields = async () => {
    const data = await callApi(
      `/ai-architect/sessions/${sessionId}/build/tables-fields`,
      { limit: 30 }
    );

    addAssistantResult(data, "Tables & Fields Ready", "tables");
    updateStepFromResult(data, "tables");
  };

  const buildBusinessRules = async () => {
    const data = await callApi(
      `/ai-architect/sessions/${sessionId}/build/business-rules`,
      { limit: 30 }
    );

    addAssistantResult(data, "Business Rules Ready", "business_rules");
    updateStepFromResult(data, "business_rules");
  };

  const buildScheduledJobs = async () => {
    const data = await callApi(
      `/ai-architect/sessions/${sessionId}/build/scheduled-jobs`,
      { limit: 30 }
    );

    addAssistantResult(data, "Scheduled Jobs Ready", "scheduled_jobs");
    updateStepFromResult(data, "scheduled_jobs");
  };

  const buildRoles = async () => {
    const data = await callApi(`/ai-architect/sessions/${sessionId}/build/roles`, {
      limit: 30,
    });

    addAssistantResult(data, "Roles Ready", "roles");
    updateStepFromResult(data, "roles");
  };

  const buildAcls = async () => {
    const data = await callApi(`/ai-architect/sessions/${sessionId}/build/acls`, {
      limit: 30,
    });

    addAssistantResult(data, "ACLs Ready", "acls");
    updateStepFromResult(data, "acls");
  };

  const buildUiActions = async () => {
    const data = await callApi(`/ai-architect/sessions/${sessionId}/build/ui-actions`, {
      limit: 30,
    });

    addAssistantResult(data, "Actions Ready", "ui_actions");
    updateStepFromResult(data, "ui_actions");
  };

  const assemblePackage = async () => {
    const data = await callApi(
      `/ai-architect/sessions/${sessionId}/assemble-package`
    );

    addAssistantResult(data, "Package Assembled", "assemble");
    updateStepFromResult(data, "assemble");
  };

  const testScripts = async () => {
    if (!sessionId) return;

    const data = await callApi(
      `/ai-architect/sessions/${sessionId}/test-scripts`,
      {
        mode: "dry_run",
        stop_on_first_error: true,
        analyze_with_ai: true,
      }
    );

    addAssistantResult(data, "Script Test Results", "script_test");
    updateStepFromResult(data, "script_test");
  };

  const previewApply = async () => {
    const data = await callApi(`/ai-architect/sessions/${sessionId}/apply-package`, {
      mode: "preview",
    });

    addAssistantResult(data, "Preview Ready", "preview");
    updateStepFromResult(data, "preview");
  };

  const applyPackage = async () => {
    const ok = window.confirm(
      "This will apply supported metadata changes to the platform. Continue?"
    );

    if (!ok) return;

    const data = await callApi(`/ai-architect/sessions/${sessionId}/apply-package`, {
      mode: "apply",
    });

    addAssistantResult(data, "Package Applied", "success");
    updateStepFromResult(data, "apply");
  };

  const rollbackPreview = async () => {
    if (!sessionId) return;
    setRollbackLoading(true);
    setRollbackError("");
    try {
      const data = await postJson(`/ai-architect/sessions/${sessionId}/rollback`, {
        mode: "preview",
        force_operation_ids: [],
      });
      setRollbackResult(data);
      setForceIds([]);
    } catch (e) {
      setRollbackError(e.message || "Rollback preview failed");
    } finally {
      setRollbackLoading(false);
    }
  };

  const rollbackApply = async (operationIdsToForce = forceIds) => {
    if (!sessionId) return;
    const ok = window.confirm(
      operationIdsToForce.length > 0
        ? `This will permanently delete data for ${operationIdsToForce.length} forced item(s), plus revert everything else that's safe. Continue?`
        : "This will revert everything that's safe to automatically roll back. Continue?"
    );
    if (!ok) return;

    setRollbackLoading(true);
    setRollbackError("");
    try {
      const data = await postJson(`/ai-architect/sessions/${sessionId}/rollback`, {
        mode: "apply",
        force_operation_ids: operationIdsToForce,
      });
      setRollbackResult(data);
      setForceIds([]);
    } catch (e) {
      setRollbackError(e.message || "Rollback failed");
    } finally {
      setRollbackLoading(false);
    }
  };

  const toggleForceId = (operationId) => {
    setForceIds((prev) =>
      prev.includes(operationId) ? prev.filter((id) => id !== operationId) : [...prev, operationId]
    );
  };

  const newSession = () => {
    setSessionId("");
    setMessages([]);
    setLastResult(null);
    setWorkspaceHistory([]);
    setCurrentStep("describe");
    setInput(DEFAULT_PROMPT);
    setError("");
    navigate("/ai-architect-lab", { replace: true });
  };

  const runPrimaryAction = () => {
    if (!sessionId) return sendMessage();
    if (currentStep === "clarify") return sendMessage();
    if (currentStep === "design") return approveDesign();
    if (currentStep === "approve") return generateBlueprint();
    if (currentStep === "blueprint") return buildTablesFields();
    if (currentStep === "tables") return buildBusinessRules();
    if (currentStep === "business_rules") return buildScheduledJobs();
    if (currentStep === "scheduled_jobs") return buildRoles();
    if (currentStep === "roles") return buildAcls();
    if (currentStep === "acls") return buildUiActions();
    if (currentStep === "ui_actions") return assemblePackage();
    if (currentStep === "assemble") return testScripts();
    if (currentStep === "script_test") {
      if (lastResult?.status === "script_test_passed") {
        return previewApply();
      }
      return sendMessage();
    }
    if (currentStep === "preview") return applyPackage();
    if (currentStep === "apply") return newSession();
    return sendMessage();
  };

  const permissions = {
    canApprove: Boolean(sessionId && currentStep === "design"),
    canBlueprint: Boolean(sessionId && currentStep === "approve"),
    canTables: Boolean(sessionId && currentStep === "blueprint"),
    canBusinessRules: Boolean(sessionId && currentStep === "tables"),
    canScheduledJobs: Boolean(sessionId && currentStep === "business_rules"),
    canRoles: Boolean(
      sessionId &&
        currentStep === "scheduled_jobs" &&
        lastResult?.status === "scheduled_jobs_generated"
    ),
    canAcls: Boolean(
      sessionId && currentStep === "roles" && lastResult?.status === "roles_generated"
    ),
    canUiActions: Boolean(
      sessionId && currentStep === "acls" && lastResult?.status === "acls_generated"
    ),
    canAssemble: Boolean(
      sessionId && currentStep === "ui_actions" && lastResult?.status === "ui_actions_generated"
    ),
    canTestScripts: Boolean(
      sessionId &&
        currentStep === "assemble" &&
        lastResult?.status === "assembled_package_generated"
    ),
    canPreview: Boolean(
      sessionId &&
        currentStep === "script_test" &&
        lastResult?.status === "script_test_passed"
    ),
    canApply: Boolean(
      sessionId &&
        currentStep === "preview" &&
        ["preview_ready", "preview_only"].includes(lastResult?.status) &&
        !(lastResult?.errors_count > 0)
    ),
  };

  const disablePrimary =
    loading ||
    ((!sessionId || currentStep === "clarify") && !input.trim()) ||
    (currentStep === "script_test" &&
      lastResult?.status !== "script_test_passed" &&
      !input.trim());

  const displayedAction = getDisplayedStepCopy(uiMode, currentStep, nextAction);
  const PrimaryIcon = displayedAction.icon;

  // Live plan state for the flow diagram + plain-language summary: prefer
  // the fully assembled package once it exists (most accurate/complete),
  // otherwise progressively merge each build step's result as it comes in
  // — from workspaceHistory, oldest first — so the picture builds up
  // during the conversation instead of only appearing at the very end.
  const livePlanSource = useMemo(() => {
    if (lastResult?.assembled_blueprint) return lastResult.assembled_blueprint;

    const merged = {
      tables_needed: [],
      fields_needed: [],
      role_changes: [],
      acl_changes: [],
      business_rules: [],
      scheduled_jobs: [],
      ui_actions_changes: [],
    };

    const chronological = [...workspaceHistory].reverse();
    for (const entry of chronological) {
      const data = entry.data;
      if (data?.blueprint?.tables_needed) merged.tables_needed = data.blueprint.tables_needed;
      if (data?.blueprint?.fields_needed) merged.fields_needed = data.blueprint.fields_needed;
      if (data?.roles?.roles_needed) merged.role_changes = data.roles.roles_needed;
      if (data?.acls?.acl_changes) merged.acl_changes = data.acls.acl_changes;
      if (data?.business_rules?.business_rules) merged.business_rules = data.business_rules.business_rules;
      if (data?.scheduled_jobs?.scheduled_jobs) merged.scheduled_jobs = data.scheduled_jobs.scheduled_jobs;
      if (data?.ui_actions?.ui_actions) merged.ui_actions_changes = data.ui_actions.ui_actions;
    }
    return merged;
  }, [lastResult, workspaceHistory]);

  return (
    <div style={embedded ? { ...styles.page, minHeight: 0, background: "transparent", padding: 0 } : styles.page}>
      <div style={styles.shell}>
        <section style={styles.hero}>
          <div>
            <div style={styles.badge}>
              <Sparkles size={16} />
              AI Native App Builder
            </div>

            <h1 style={styles.title}>AI Architect</h1>

            <p style={styles.subtitle}>
              Chat naturally with the architect. The app now supports a real
              clarification gate, strict script validation, modular build steps,
              preview, and explicit apply.
            </p>
          </div>

          <div style={styles.heroActions}>
            <div style={styles.modeToggle} role="tablist" aria-label="View mode">
              <button
                type="button"
                role="tab"
                aria-selected={uiMode === "friendly"}
                style={{
                  ...styles.modeToggleBtn,
                  ...(uiMode === "friendly" ? styles.modeToggleBtnActive : {}),
                }}
                onClick={() => setUiMode("friendly")}
              >
                <Sparkle size={14} />
                Friendly
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={uiMode === "technical"}
                style={{
                  ...styles.modeToggleBtn,
                  ...(uiMode === "technical" ? styles.modeToggleBtnActive : {}),
                }}
                onClick={() => setUiMode("technical")}
              >
                <Braces size={14} />
                Technical
              </button>
            </div>

            <label style={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={autoAdvance}
                onChange={(e) => setAutoAdvance(e.target.checked)}
              />
              Auto step sync
            </label>

            <button style={styles.resetButton} onClick={newSession}>
              <RotateCcw size={16} />
              New Session
            </button>
          </div>
        </section>

        {uiMode === "technical" ? (
          <StepBar currentStep={currentStep} />
        ) : (
          <FriendlyProgress currentStep={currentStep} />
        )}

        <div style={{ ...styles.layout, gridTemplateColumns: uiMode === "technical" ? "minmax(0, 1fr) 540px" : "1fr" }}>
          <main style={styles.mainPanel}>
            <div style={styles.nextCard}>
              <div style={styles.nextIcon}>
                <PrimaryIcon size={22} />
              </div>

              <div style={{ flex: 1 }}>
                <div style={styles.nextLabel}>
                  {uiMode === "friendly" ? "What's next" : "Recommended next step"}
                </div>
                <h2 style={styles.nextTitle}>{displayedAction.title}</h2>
                <p style={styles.nextDescription}>{displayedAction.description}</p>
              </div>

              <button
                style={{
                  ...styles.primaryButton,
                  ...(disablePrimary ? styles.disabledPrimary : {}),
                }}
                onClick={runPrimaryAction}
                disabled={disablePrimary}
              >
                {loading ? <RefreshCw size={18} /> : <ChevronRight size={18} />}
                {loading ? "Running..." : displayedAction.button}
              </button>
            </div>

            {error && (
              <div style={styles.errorBanner}>
                <AlertTriangle size={18} />
                <span>{error}</span>
              </div>
            )}

            {isClarificationResponse(lastResult) && (
              <ClarificationBanner data={lastResult} />
            )}

            <RollbackPanel
              visible={currentStep === "apply"}
              result={rollbackResult}
              loading={rollbackLoading}
              error={rollbackError}
              forceIds={forceIds}
              onPreview={rollbackPreview}
              onApply={rollbackApply}
              onToggleForce={toggleForceId}
            />

            <TestMyAppPanel
              visible={currentStep === "apply"}
              sessionId={sessionId}
              uiMode={uiMode}
            />

            {uiMode === "friendly" && (
              <>
                <div style={styles.chatPanel}>
                  <div style={styles.chatHeader}>
                    <div>
                      <h2 style={styles.panelTitle}>
                        <Workflow size={19} />
                        The plan so far
                      </h2>
                      <p style={styles.panelSubtitle}>
                        This updates as we go — nothing here is built yet until you get to Apply.
                      </p>
                    </div>
                  </div>
                  <div style={{ padding: "4px 4px 16px" }}>
                    <FlowDiagram
                      planSource={livePlanSource}
                      height={320}
                      emptyText="Tell me what you'd like to build, and I'll start sketching it out here."
                    />
                  </div>
                </div>

                <PlanSummaryCard planSource={livePlanSource} />
              </>
            )}

            <div style={styles.chatPanel}>
              <div style={styles.chatHeader}>
                <div>
                  <h2 style={styles.panelTitle}>
                    <MessageSquareText size={19} />
                    Conversation
                  </h2>
                  <p style={styles.panelSubtitle}>
                    Use this area for requirements, answers, design feedback,
                    and corrections. Technical JSON stays in the workspace.
                  </p>
                </div>

                {sessionId && (
                  <div style={styles.sessionPill}>
                    Session active
                  </div>
                )}
              </div>

              <div style={styles.chatBox}>
                {resuming && (
                  <div style={styles.emptyState}>
                    <RefreshCw size={32} />
                    <h3>Resuming session...</h3>
                  </div>
                )}

                {!resuming && messages.length === 0 && (
                  <div style={styles.emptyState}>
                    <Bot size={38} />
                    <h3>Tell me what you want to build</h3>
                    <p>
                      The architect will ask questions when details are missing.
                      It should not assume fake APIs, fake helper functions, or
                      fake script runtime.
                    </p>

                    {(loadingRecent || recentSessions.length > 0) && (
                      <div style={styles.recentWrap}>
                        <div style={styles.recentHeader}>
                          <History size={14} />
                          Recent Sessions
                        </div>

                        {loadingRecent && (
                          <div style={styles.recentEmpty}>Loading...</div>
                        )}

                        {!loadingRecent &&
                          recentSessions.map((s) => (
                            <button
                              key={s.id}
                              style={styles.recentItem}
                              onClick={() => navigate(`/ai-architect-lab/${s.id}`)}
                            >
                              <span style={styles.recentItemText}>
                                {(s.request_text || "Untitled session").slice(0, 90)}
                                {(s.request_text || "").length > 90 ? "..." : ""}
                              </span>
                              <span style={styles.recentItemMeta}>
                                {s.current_phase || s.status}
                                <ArrowUpRight size={13} />
                              </span>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {messages.map((m, index) => (
                  <MessageCard key={index} message={m} />
                ))}

                {loading && (
                  <div style={styles.loadingCard}>
                    <RefreshCw size={18} />
                    AI Architect is working...
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              <div style={styles.inputArea}>
                <textarea
                  style={styles.chatInput}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={
                    currentStep === "clarify"
                      ? "Answer the architect questions here..."
                      : "Type your request, feedback, or next instruction..."
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />

                <button
                  style={{
                    ...styles.sendButton,
                    ...(loading || !input.trim() ? styles.disabledPrimary : {}),
                  }}
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                >
                  <Send size={18} />
                  Send
                </button>
              </div>
            </div>
          </main>

          {uiMode === "technical" && (
            <aside style={styles.sidePanel}>
              <WorkspacePanel
                sessionId={sessionId}
                lastResult={lastResult}
                history={workspaceHistory}
                loading={loading}
                permissions={permissions}
                approveDesign={approveDesign}
                generateBlueprint={generateBlueprint}
                buildTablesFields={buildTablesFields}
                buildBusinessRules={buildBusinessRules}
                buildScheduledJobs={buildScheduledJobs}
                buildRoles={buildRoles}
                buildAcls={buildAcls}
                buildUiActions={buildUiActions}
                assemblePackage={assemblePackage}
                testScripts={testScripts}
                previewApply={previewApply}
                applyPackage={applyPackage}
              />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

function StepBar({ currentStep }) {
  const activeStepIndex = Math.max(
    0,
    STEP_ORDER.findIndex((s) => s === currentStep)
  );

  return (
    <section style={styles.stepper}>
      {STEPS.map((step, index) => {
        const isActive = index === activeStepIndex;
        const isDone = index < activeStepIndex;
        const Icon = step.icon;

        return (
          <div
            key={step.key}
            style={{
              ...styles.stepItem,
              ...(isActive ? styles.stepItemActive : {}),
              ...(isDone ? styles.stepItemDone : {}),
            }}
          >
            <div
              style={{
                ...styles.stepCircle,
                ...(isActive ? styles.stepCircleActive : {}),
                ...(isDone ? styles.stepCircleDone : {}),
              }}
            >
              {isDone ? <CheckCircle2 size={15} /> : <Icon size={14} />}
            </div>

            <div style={styles.stepTextWrap}>
              <div style={styles.stepLabel}>{step.label}</div>
              <div style={styles.stepTitle}>{step.title}</div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

// Coarse, plain-English phases for Friendly mode — collapses all 15
// technical steps (see STEPS/StepBar above, which Technical mode still
// shows in full) down to 4 phases nobody needs a glossary for.
const FRIENDLY_PHASES = [
  { key: "talk", label: "Talking it through", steps: ["describe", "clarify", "design", "approve"] },
  { key: "build", label: "Building your app", steps: ["blueprint", ...FRIENDLY_BUILDING_STEPS] },
  { key: "review", label: "Final review", steps: ["preview"] },
  { key: "done", label: "Done", steps: ["apply"] },
];

function FriendlyProgress({ currentStep }) {
  const activePhaseIndex = Math.max(
    0,
    FRIENDLY_PHASES.findIndex((phase) => phase.steps.includes(currentStep))
  );

  return (
    <section style={styles.friendlyProgress}>
      {FRIENDLY_PHASES.map((phase, index) => {
        const isActive = index === activePhaseIndex;
        const isDone = index < activePhaseIndex;
        return (
          <div
            key={phase.key}
            style={{
              ...styles.friendlyProgressItem,
              ...(isActive ? styles.friendlyProgressItemActive : {}),
              ...(isDone ? styles.friendlyProgressItemDone : {}),
            }}
          >
            <div
              style={{
                ...styles.friendlyProgressDot,
                ...(isActive ? styles.friendlyProgressDotActive : {}),
                ...(isDone ? styles.friendlyProgressDotDone : {}),
              }}
            >
              {isDone ? <CheckCircle2 size={13} /> : null}
            </div>
            <span style={styles.friendlyProgressLabel}>{phase.label}</span>
          </div>
        );
      })}
    </section>
  );
}

function ClarificationBanner({ data }) {
  const questions = getQuestions(data);

  if (!questions.length) return null;

  return (
    <div style={styles.clarificationBanner}>
      <HelpCircle size={19} />
      <div>
        <strong>Architect needs clarification</strong>
        <p>
          Answer these questions in the chat. The builder should not generate
          scripts until these details are clear.
        </p>
        <ol>
          {questions.map((q, index) => (
            <li key={index}>
              {typeof q === "string" ? q : q.question || q.message || JSON.stringify(q)}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function WorkspacePanel({
  sessionId,
  lastResult,
  history,
  loading,
  permissions,
  approveDesign,
  generateBlueprint,
  buildTablesFields,
  buildBusinessRules,
  buildScheduledJobs,
  buildRoles,
  buildAcls,
  buildUiActions,
  assemblePackage,
  testScripts,
  previewApply,
  applyPackage,
}) {
  const [tab, setTab] = useState("overview");

  const tabs = [
    { key: "overview", label: "Overview", icon: ListChecks },
    { key: "questions", label: "Questions", icon: HelpCircle },
    { key: "blueprint", label: "Blueprint", icon: Workflow },
    { key: "builders", label: "Builders", icon: Braces },
    { key: "needs_review", label: "Needs Review", icon: ShieldAlert },
    { key: "package", label: "Package", icon: PackageCheck },
    { key: "script_tests", label: "Tests", icon: Code2 },
    { key: "preview", label: "Preview", icon: Eye },
    { key: "applied", label: "Applied", icon: ClipboardCheck },
    { key: "raw", label: "Raw", icon: FileJson },
  ];

  return (
    <>
      <div style={styles.workspaceHeader}>
        <div>
          <div style={styles.sideHeader}>
            <ShieldCheck size={20} />
            Builder Workspace
          </div>
          <p style={styles.workspaceSubtitle}>
            Technical metadata, warnings, generated scripts, preview, and raw
            responses live here.
          </p>
        </div>
      </div>

      <div style={styles.quickActions}>
        <ActionButton
          icon={<CheckCircle2 size={17} />}
          title="Approve"
          disabled={!permissions.canApprove || loading}
          onClick={approveDesign}
        />

        <ActionButton
          icon={<Workflow size={17} />}
          title="Blueprint"
          disabled={!permissions.canBlueprint || loading}
          onClick={generateBlueprint}
        />

        <ActionButton
          icon={<Table2 size={17} />}
          title="Tables"
          disabled={!permissions.canTables || loading}
          onClick={buildTablesFields}
        />

        <ActionButton
          icon={<GitBranch size={17} />}
          title="BRs"
          disabled={!permissions.canBusinessRules || loading}
          onClick={buildBusinessRules}
        />

        <ActionButton
          icon={<Clock3 size={17} />}
          title="Jobs"
          disabled={!permissions.canScheduledJobs || loading}
          onClick={buildScheduledJobs}
        />

        <ActionButton
          icon={<Users size={17} />}
          title="Roles"
          disabled={!permissions.canRoles || loading}
          onClick={buildRoles}
        />

        <ActionButton
          icon={<Shield size={17} />}
          title="ACLs"
          disabled={!permissions.canAcls || loading}
          onClick={buildAcls}
        />

        <ActionButton
          icon={<MousePointerClick size={17} />}
          title="Actions"
          disabled={!permissions.canUiActions || loading}
          onClick={buildUiActions}
        />

        <ActionButton
          icon={<Hammer size={17} />}
          title="Assemble"
          disabled={!permissions.canAssemble || loading}
          onClick={assemblePackage}
        />

        <ActionButton
          icon={<Code2 size={17} />}
          title="Test Scripts"
          disabled={!permissions.canTestScripts || loading}
          onClick={testScripts}
        />

        <ActionButton
          icon={<Eye size={17} />}
          title="Preview"
          disabled={!permissions.canPreview || loading}
          onClick={previewApply}
        />

        <button
          style={{
            ...styles.applyButton,
            ...(!permissions.canApply || loading ? styles.disabledButton : {}),
          }}
          disabled={!permissions.canApply || loading}
          onClick={applyPackage}
        >
          <PlayCircle size={17} />
          Apply
        </button>
      </div>

      <div style={styles.workspaceTabs}>
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;

          return (
            <button
              key={t.key}
              style={{
                ...styles.workspaceTab,
                ...(active ? styles.workspaceTabActive : {}),
              }}
              onClick={() => setTab(t.key)}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      <div style={styles.workspaceBody}>
        {tab === "needs_review" ? (
          <NeedsReviewTab sessionId={sessionId} />
        ) : !lastResult ? (
          <EmptyWorkspace />
        ) : (
          <>
            {tab === "overview" && (
              <OverviewTab data={lastResult} history={history} />
            )}
            {tab === "questions" && <QuestionsTab data={lastResult} />}
            {tab === "blueprint" && <BlueprintTab data={lastResult} />}
            {tab === "builders" && <BuildersTab data={lastResult} />}
            {tab === "package" && <PackageTab data={lastResult} />}
            {tab === "script_tests" && <ScriptTestsTab data={lastResult} />}
            {tab === "preview" && <PreviewTab data={lastResult} />}
            {tab === "applied" && <AppliedTab data={lastResult} />}
            {tab === "raw" && <RawTab data={lastResult} />}
          </>
        )}
      </div>

      <div style={styles.safetyNote}>
        <strong>Safe modular mode:</strong> each build step is scoped. Tables do
        not create automations. Business rules do not create scheduled jobs.
        Scheduled jobs do not create business rules. Scripts must be executable,
        must not use fake helper functions, and can be dry-run tested before preview/apply.
      </div>
    </>
  );
}

function EmptyWorkspace() {
  return (
    <div style={styles.workspaceEmpty}>
      <Layers3 size={34} />
      <h3>No metadata yet</h3>
      <p>
        Start a request. Design, questions, blueprint, builders, package,
        preview, applied records, skipped records, and raw responses will appear
        here.
      </p>
    </div>
  );
}

function OverviewTab({ data, history }) {
  const warnings = getWarnings(data);
  const questions = getQuestions(data);

  return (
    <div>
      <StatusCard data={data} />

      {questions.length > 0 && (
        <InfoBlock title="Questions">
          <QuestionList questions={questions} />
        </InfoBlock>
      )}

      {warnings.length > 0 && (
        <InfoBlock title="Warnings">
          <WarningList warnings={warnings} />
        </InfoBlock>
      )}

      {getRepairAttempts(data).length > 0 && (
        <InfoBlock title="Automatic Repair Attempts">
          <JsonBox data={getRepairAttempts(data)} />
        </InfoBlock>
      )}

      {data?.summary && (
        <InfoBlock title="Summary">
          <SummaryGrid summary={data.summary} />
        </InfoBlock>
      )}

      {data?.context_preview && (
        <InfoBlock title={`Context Used: ${data.context_items_used || 0}`}>
          <JsonBox data={data.context_preview} />
        </InfoBlock>
      )}

      {history?.length > 0 && (
        <InfoBlock title="Recent Activity">
          <div style={styles.timeline}>
            {history.slice(0, 10).map((item, idx) => (
              <div key={idx} style={styles.timelineItem}>
                <span>{item.timestamp}</span>
                <strong>{item.data?.status || item.data?.message || "Updated"}</strong>
              </div>
            ))}
          </div>
        </InfoBlock>
      )}
    </div>
  );
}

function StatusCard({ data }) {
  const type = classifyResultType(data);
  const Icon =
    type === "error"
      ? XCircle
      : type === "clarification"
      ? HelpCircle
      : type === "warning"
      ? AlertTriangle
      : type === "success"
      ? Check
      : Info;

  const colorStyle =
    type === "error"
      ? styles.statusError
      : type === "clarification"
      ? styles.statusClarification
      : type === "warning"
      ? styles.statusWarning
      : type === "success"
      ? styles.statusSuccess
      : styles.statusInfo;

  return (
    <div style={{ ...styles.statusCard, ...colorStyle }}>
      <Icon size={20} />
      <div>
        <strong>{data?.status || (data?.error ? "error" : "latest result")}</strong>
        <p>{getReadableText(data)}</p>
      </div>
    </div>
  );
}

function QuestionsTab({ data }) {
  const questions = getQuestions(data);

  if (!questions.length) {
    return <SoftEmpty text="No clarification questions in the latest response." />;
  }

  return (
    <InfoBlock title="Clarification Questions">
      <QuestionList questions={questions} />
    </InfoBlock>
  );
}

function BlueprintTab({ data }) {
  const blueprint =
    data?.blueprint ||
    data?.assembled_blueprint ||
    data?.tables_fields ||
    data?.business_rules ||
    data?.scheduled_jobs ||
    data?.acls;

  if (!blueprint) {
    return <SoftEmpty text="No blueprint or modular output yet." />;
  }

  return (
    <>
      <InfoBlock title="Flow Diagram">
        <FlowDiagram planSource={blueprint} emptyText="Nothing to visualize yet — keep building." />
      </InfoBlock>

      <BlueprintStats data={blueprint} />

      <InfoBlock title="Blueprint / Modular Output">
        <JsonBox data={blueprint} />
      </InfoBlock>
    </>
  );
}

function BlueprintStats({ data }) {
  if (!data) return null;

  const stats = {
    tables_needed: countArray(data.tables_needed),
    fields_needed: countArray(data.fields_needed),
    roles_needed: countArray(data.roles_needed),
    business_rules_needed: countArray(data.business_rules_needed),
    scheduled_jobs_needed: countArray(data.scheduled_jobs_needed),
    acl_changes: countArray(data.acl_changes),
    ui_actions_needed: countArray(data.ui_actions_needed),
    warnings: countArray(data.warnings),
  };

  const hasAny = Object.values(stats).some((v) => v > 0);

  if (!hasAny) return null;

  return (
    <InfoBlock title="Blueprint Stats">
      <SummaryGrid summary={stats} />
    </InfoBlock>
  );
}

function BuildersTab({ data }) {
  const builders = {
    tables_fields: data?.tables_fields,
    business_rules: data?.business_rules || data?.business_rules_build,
    scheduled_jobs: data?.scheduled_jobs || data?.scheduled_jobs_build,
    acls: data?.acls || data?.acls_build,
  };

  const hasAny = Object.values(builders).some(Boolean);

  if (!hasAny) {
    return <SoftEmpty text="No modular builder output in the latest response." />;
  }

  return (
    <>
      {builders.tables_fields && (
        <InfoBlock title="Tables / Fields">
          <BuilderSummary data={builders.tables_fields} type="tables" />
          <JsonBox data={builders.tables_fields} />
        </InfoBlock>
      )}

      {builders.business_rules && (
        <InfoBlock title="Business Rules">
          <BuilderSummary data={builders.business_rules} type="business_rules" />
          <JsonBox data={builders.business_rules} />
        </InfoBlock>
      )}

      {builders.scheduled_jobs && (
        <InfoBlock title="Scheduled Jobs">
          <BuilderSummary data={builders.scheduled_jobs} type="scheduled_jobs" />
          <JsonBox data={builders.scheduled_jobs} />
        </InfoBlock>
      )}

      {builders.acls && (
        <InfoBlock title="ACLs">
          <BuilderSummary data={builders.acls} type="acls" />
          <JsonBox data={builders.acls} />
        </InfoBlock>
      )}
    </>
  );
}

function BuilderSummary({ data, type }) {
  let summary = {};

  if (type === "tables") {
    summary = {
      tables_to_create: countArray(data?.metadata_changes?.tables_to_create),
      fields_to_create: countArray(data?.metadata_changes?.fields_to_create),
      fields_to_update: countArray(data?.metadata_changes?.fields_to_update),
      warnings: countArray(data?.warnings),
    };
  }

  if (type === "business_rules") {
    summary = {
      business_rules: countArray(data?.business_rules),
      warnings: countArray(data?.warnings),
      repair_attempts: countArray(data?.repair_attempts),
    };
  }

  if (type === "scheduled_jobs") {
    summary = {
      scheduled_jobs: countArray(data?.scheduled_jobs),
      warnings: countArray(data?.warnings),
      repair_attempts: countArray(data?.repair_attempts),
    };
  }

  if (type === "acls") {
    summary = {
      acl_changes: countArray(data?.acl_changes),
      warnings: countArray(data?.warnings),
    };
  }

  return <SummaryGrid summary={summary} />;
}

// Business rules and UI actions never get created automatically — see
// ai_pending_approval / the /pending-approvals endpoints in
// routers/ai_architect.py. This is the one place a human explicitly
// approves each one into a real, executable row (or rejects it). The AI
// Test Runner (Friendly mode's "Test my app") can only click a UI action
// or rely on a business rule once it's been approved here.
function NeedsReviewTab({ sessionId }) {
  const [items, setItems] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState("");

  const load = React.useCallback(() => {
    if (!sessionId) return;
    setLoadError("");
    fetch(`${API_BASE}/ai-architect/sessions/${sessionId}/pending-approvals`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.detail || "Failed to load pending approvals.");
        setItems(body.pending_approvals || []);
      })
      .catch((e) => setLoadError(e.message || "Failed to load pending approvals."));
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (approvalId, action) => {
    setBusyId(approvalId);
    setActionError("");
    try {
      const res = await fetch(
        `${API_BASE}/ai-architect/sessions/${sessionId}/pending-approvals/${approvalId}/${action}`,
        { method: "POST", headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Failed to ${action} this item.`);
      load();
    } catch (e) {
      setActionError(e.message || `Failed to ${action} this item.`);
    } finally {
      setBusyId(null);
    }
  };

  if (!sessionId) {
    return <SoftEmpty text="Start a session to see items waiting for review." />;
  }
  if (loadError) {
    return <SoftEmpty text={loadError} />;
  }
  if (items === null) {
    return <SoftEmpty text="Loading…" />;
  }
  if (items.length === 0) {
    return <SoftEmpty text="Nothing waiting for review. Business rules and UI actions from a build show up here once a package has been applied." />;
  }

  const pending = items.filter((i) => i.status === "pending");
  const resolved = items.filter((i) => i.status !== "pending");

  return (
    <>
      {actionError && (
        <div style={styles.errorBanner}>
          <AlertTriangle size={18} />
          <span>{actionError}</span>
        </div>
      )}

      <InfoBlock title={`Waiting for review (${pending.length})`}>
        {pending.length === 0 ? (
          <SoftEmpty text="Nothing pending right now." />
        ) : (
          pending.map((item) => (
            <PendingApprovalCard
              key={item.id}
              item={item}
              busy={busyId === item.id}
              onApprove={() => resolve(item.id, "approve")}
              onReject={() => resolve(item.id, "reject")}
            />
          ))
        )}
      </InfoBlock>

      {resolved.length > 0 && (
        <InfoBlock title={`Already reviewed (${resolved.length})`}>
          {resolved.map((item) => (
            <PendingApprovalCard key={item.id} item={item} readOnly />
          ))}
        </InfoBlock>
      )}
    </>
  );
}

function PendingApprovalCard({ item, busy, readOnly, onApprove, onReject }) {
  const record = item.record || {};
  const script = record.script || record.sip_server_script;
  const statusStyle =
    item.status === "approved"
      ? styles.approvalStatusApproved
      : item.status === "rejected"
      ? styles.approvalStatusRejected
      : styles.approvalStatusPending;

  return (
    <div style={styles.approvalCard}>
      <div style={styles.approvalCardHeader}>
        <div>
          <div style={styles.approvalCardTitle}>
            {record.name || record.label || item.target_table}
          </div>
          <div style={styles.approvalCardMeta}>
            {item.target_table} · {record.table_name || ""}
          </div>
        </div>
        <span style={{ ...styles.approvalStatusBadge, ...statusStyle }}>{item.status}</span>
      </div>

      {script && <pre style={styles.smallPre}>{script}</pre>}

      {!readOnly && (
        <div style={styles.approvalCardActions}>
          <button
            style={{ ...styles.approveButton, ...(busy ? styles.disabledButton : {}) }}
            disabled={busy}
            onClick={onApprove}
          >
            <ThumbsUp size={14} />
            Approve &amp; create
          </button>
          <button
            style={{ ...styles.rejectButton, ...(busy ? styles.disabledButton : {}) }}
            disabled={busy}
            onClick={onReject}
          >
            <ThumbsDown size={14} />
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

function PackageTab({ data }) {
  const packageData =
    data?.change_package ||
    data?.assembled_blueprint ||
    data?.package ||
    data?.metadata_package ||
    data?.executable_package;

  if (!packageData) {
    return <SoftEmpty text="No executable change package yet." />;
  }

  return (
    <>
      {data?.summary && (
        <InfoBlock title="Package Summary">
          <SummaryGrid summary={data.summary} />
        </InfoBlock>
      )}

      <InfoBlock title="Executable / Assembled Package">
        <JsonBox data={packageData} />
      </InfoBlock>
    </>
  );
}


function ScriptTestsTab({ data }) {
  const tests = data?.script_tests || [];
  const failed = data?.failed_script;
  const warnings = data?.warnings || [];
  const hasTests = tests.length > 0 || failed || isScriptTestResult(data);

  if (!hasTests) {
    return <SoftEmpty text="No script test results yet." />;
  }

  return (
    <>
      {data?.summary && (
        <InfoBlock title="Script Test Summary">
          <SummaryGrid summary={data.summary} />
        </InfoBlock>
      )}

      {warnings.length > 0 && (
        <InfoBlock title="Script Test Warnings">
          <WarningList warnings={warnings} />
        </InfoBlock>
      )}

      {failed && (
        <InfoBlock title={`Failed Script: ${failed.name || "Unnamed script"}`}>
          <SummaryGrid
            summary={{
              order: failed.order ?? "",
              type: failed.type || "",
              ok: Boolean(failed.ok),
            }}
          />

          {failed.traceback && (
            <>
              <div style={styles.infoBlockTitle}>Traceback</div>
              <pre style={styles.smallPre}>{failed.traceback}</pre>
            </>
          )}

          {failed.stdout && (
            <>
              <div style={styles.infoBlockTitle}>Stdout</div>
              <pre style={styles.smallPre}>{failed.stdout}</pre>
            </>
          )}

          {failed.stderr && (
            <>
              <div style={styles.infoBlockTitle}>Stderr</div>
              <pre style={styles.smallPre}>{failed.stderr}</pre>
            </>
          )}

          {failed.ai_analysis && (
            <>
              <div style={styles.infoBlockTitle}>AI Analysis</div>
              <pre style={styles.smallPre}>{safeJson(failed.ai_analysis)}</pre>
            </>
          )}

          {failed.ai_analysis?.fixed_script && (
            <>
              <div style={styles.infoBlockTitle}>Suggested Fixed Script</div>
              <pre style={styles.smallPre}>{failed.ai_analysis.fixed_script}</pre>
            </>
          )}
        </InfoBlock>
      )}

      {tests.map((test, index) => (
        <InfoBlock
          key={index}
          title={`${test.ok ? "Passed" : "Failed"}: ${test.name || "Unnamed script"}`}
        >
          <SummaryGrid
            summary={{
              order: test.order ?? "",
              type: test.type || "",
              ok: Boolean(test.ok),
            }}
          />

          {test.stdout && (
            <>
              <div style={styles.infoBlockTitle}>Stdout</div>
              <pre style={styles.smallPre}>{test.stdout}</pre>
            </>
          )}

          {test.stderr && (
            <>
              <div style={styles.infoBlockTitle}>Stderr</div>
              <pre style={styles.smallPre}>{test.stderr}</pre>
            </>
          )}

          {test.traceback && (
            <>
              <div style={styles.infoBlockTitle}>Traceback</div>
              <pre style={styles.smallPre}>{test.traceback}</pre>
            </>
          )}

          {test.ai_analysis && (
            <>
              <div style={styles.infoBlockTitle}>AI Analysis</div>
              <pre style={styles.smallPre}>{safeJson(test.ai_analysis)}</pre>
            </>
          )}
        </InfoBlock>
      ))}
    </>
  );
}

// preview-apply and apply-package (both modes) now return the exact same
// ApplyReport shape from the backend's unified apply engine — preview and
// apply can never drift, because there's one report format either way.
function isApplyReport(data) {
  return (
    data &&
    (Array.isArray(data.applied) ||
      Array.isArray(data.needs_review) ||
      Array.isArray(data.skipped) ||
      Array.isArray(data.errors))
  );
}

function itemKey(item, index) {
  return `${item.group || ""}-${item.target_table || ""}-${item.id || index}`;
}

function ApplyReportView({ data, emptyText }) {
  if (!isApplyReport(data)) {
    return <SoftEmpty text={emptyText} />;
  }

  const applied = data.applied || [];
  const needsReview = data.needs_review || [];
  const skipped = data.skipped || [];
  const errors = data.errors || [];

  return (
    <>
      <InfoBlock title={data.mode === "preview" ? "Preview Summary" : "Apply Summary"}>
        <SummaryGrid
          summary={{
            applied: applied.length,
            needs_review: needsReview.length,
            skipped: skipped.length,
            errors: errors.length,
          }}
        />
      </InfoBlock>

      {errors.length > 0 && (
        <InfoBlock title="Blocking Errors">
          {errors.map((e, i) => (
            <div key={i} style={{ ...styles.statusCard, ...styles.statusError, marginBottom: i === errors.length - 1 ? 0 : 10 }}>
              <XCircle size={16} />
              <div>
                <strong>{e.target_table || e.type || "Error"}</strong>
                <div>{e.message}</div>
              </div>
            </div>
          ))}
        </InfoBlock>
      )}

      {needsReview.length > 0 && (
        <InfoBlock title="Needs Human Review Before Going Live">
          <div style={{ fontSize: "12px", color: "#9a3412", marginBottom: "10px" }}>
            Business rules, scheduled jobs, and UI action scripts run with real database
            access and are never applied automatically. Review each script below, then
            create it manually (or approve it) once you're satisfied.
          </div>
          {needsReview.map((item, i) => (
            <div key={itemKey(item, i)} style={{ ...styles.statusCard, ...styles.statusWarning, flexDirection: "column", alignItems: "stretch" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <ShieldCheck size={16} />
                <strong>
                  {item.target_table} — {item.record?.name || "unnamed"}
                </strong>
              </div>
              {item.record?.script && (
                <pre style={{ ...styles.smallPre, marginTop: "8px" }}>{item.record.script}</pre>
              )}
            </div>
          ))}
        </InfoBlock>
      )}

      {applied.length > 0 && (
        <InfoBlock title={data.mode === "preview" ? "Would Apply" : "Applied"}>
          {applied.map((item, i) => (
            <div key={itemKey(item, i)} style={{ ...styles.statusCard, ...styles.statusSuccess, marginBottom: i === applied.length - 1 ? 0 : 8 }}>
              <CheckCircle2 size={16} />
              <div>
                <strong>
                  {item.target_table} — {item.record?.name || item.record?.label || item.id || "record"}
                </strong>
                {item.note && <div>{item.note}</div>}
              </div>
            </div>
          ))}
        </InfoBlock>
      )}

      {skipped.length > 0 && (
        <InfoBlock title="Skipped">
          {skipped.map((item, i) => (
            <div key={itemKey(item, i)} style={styles.timelineItem}>
              <span>
                {item.target_table || item.group}: {item.reason}
              </span>
            </div>
          ))}
        </InfoBlock>
      )}
    </>
  );
}

function PreviewTab({ data }) {
  return <ApplyReportView data={data} emptyText="No apply preview yet." />;
}

function AppliedTab({ data }) {
  return <ApplyReportView data={data} emptyText="Nothing has been applied yet." />;
}

function RollbackReportView({ data, forceIds, onToggleForce }) {
  if (!data) return <SoftEmpty text="No rollback preview yet." />;

  const reverted = data.reverted || [];
  const blocked = data.blocked || [];
  const skipped = data.skipped || [];
  const errors = data.errors || [];

  return (
    <>
      <InfoBlock title={data.mode === "preview" ? "Rollback Preview Summary" : "Rollback Summary"}>
        <SummaryGrid
          summary={{
            reverted: reverted.length,
            blocked: blocked.length,
            skipped: skipped.length,
            errors: errors.length,
          }}
        />
      </InfoBlock>

      {errors.length > 0 && (
        <InfoBlock title="Errors">
          {errors.map((e, i) => (
            <div key={e.operation_id || i} style={{ ...styles.statusCard, ...styles.statusError, marginBottom: i === errors.length - 1 ? 0 : 10 }}>
              <XCircle size={16} />
              <div>
                <strong>{e.target || e.operation_type || "Error"}</strong>
                <div>{e.message}</div>
              </div>
            </div>
          ))}
        </InfoBlock>
      )}

      {blocked.length > 0 && (
        <InfoBlock title="Blocked — has data, needs explicit confirmation">
          <div style={{ fontSize: "12px", color: "#9a3412", marginBottom: "10px" }}>
            Reverting these would destroy real data. Check the ones you want to force, then use "Force Revert
            Selected" below — everything else that's safe reverts either way.
          </div>
          {blocked.map((item) => (
            <label
              key={item.operation_id}
              style={{ ...styles.statusCard, ...styles.statusWarning, cursor: "pointer", alignItems: "flex-start" }}
            >
              <input
                type="checkbox"
                checked={forceIds.includes(item.operation_id)}
                onChange={() => onToggleForce(item.operation_id)}
                style={{ marginTop: "2px" }}
              />
              <div>
                <strong>{item.target}</strong>
                <div>{item.reason}</div>
                {typeof item.row_count === "number" && <div>Row count: {item.row_count}</div>}
              </div>
            </label>
          ))}
        </InfoBlock>
      )}

      {reverted.length > 0 && (
        <InfoBlock title={data.mode === "preview" ? "Would Revert" : "Reverted"}>
          {reverted.map((item, i) => (
            <div key={item.operation_id || i} style={{ ...styles.statusCard, ...styles.statusSuccess, marginBottom: i === reverted.length - 1 ? 0 : 8 }}>
              <CheckCircle2 size={16} />
              <div>
                <strong>{item.target}</strong>
                {item.note && <div>{item.note}</div>}
              </div>
            </div>
          ))}
        </InfoBlock>
      )}

      {skipped.length > 0 && (
        <InfoBlock title="Skipped (already reverted)">
          {skipped.map((item, i) => (
            <div key={item.operation_id || i} style={styles.timelineItem}>
              <span>
                {item.target}: {item.reason}
              </span>
            </div>
          ))}
        </InfoBlock>
      )}
    </>
  );
}

function PlanSummaryCard({ planSource }) {
  const plan = normalizePlanState(planSource);
  const lines = [];

  plan.roles.forEach((r) => {
    if (r.name) lines.push({ icon: Users, text: `A new role: ${r.name}` });
  });

  plan.tables.forEach((t) => {
    const count = t.fields.length;
    lines.push({
      icon: Table2,
      text: `A new ${t.label || t.name} with ${count} field${count === 1 ? "" : "s"}`,
    });
  });

  plan.acls.forEach((a) => {
    if (!a.role || !a.table_name) return;
    const verbs = { GET: "view", POST: "create", PUT: "edit", DELETE: "delete" };
    const methods = (a.allowed_methods || []).map((m) => verbs[m] || m.toLowerCase()).join(", ");
    lines.push({ icon: Shield, text: `${a.role} can ${methods || "access"} ${a.table_name}` });
  });

  plan.businessRules.forEach((br) => {
    const events = [];
    if (br.on_insert) events.push("added");
    if (br.on_update) events.push("changed");
    if (br.on_delete) events.push("deleted");
    lines.push({
      icon: GitBranch,
      text: `Automatically runs "${br.name}" when a ${br.table_name} record is ${events.join("/") || "changed"}`,
    });
  });

  plan.scheduledJobs.forEach((job) => {
    const when = job.frequency ? `${job.frequency}${job.execution_time ? ` at ${job.execution_time}` : ""}` : "on a schedule";
    lines.push({ icon: Clock3, text: `"${job.name}" runs ${when}` });
  });

  plan.uiActions.forEach((a) => {
    const sendsEmail = /\bsmtp\b|\bemail\b/i.test(a.script || "");
    lines.push({
      icon: MousePointerClick,
      text: `A "${a.label || a.name}" button${a.table_name ? ` on the ${a.table_name} form` : ""}${sendsEmail ? " that sends an email" : ""}`,
    });
  });

  if (lines.length === 0) return null;

  return (
    <div style={styles.chatPanel}>
      <div style={styles.chatHeader}>
        <div>
          <h2 style={styles.panelTitle}>
            <Sparkle size={19} />
            Here's what I'm building for you
          </h2>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "4px 4px 16px" }}>
        {lines.map((line, i) => {
          const Icon = line.icon;
          return (
            <div
              key={i}
              style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13.5px", color: "var(--ink-2, #1e2e30)" }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  background: "var(--brand-wash, #e6f4f1)",
                  color: "var(--brand, #0d7d6f)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon size={14} />
              </div>
              {line.text}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RollbackPanel({ visible, result, loading, error, forceIds, onPreview, onApply, onToggleForce }) {
  if (!visible) return null;

  const hasBlocked = (result?.blocked || []).length > 0;

  return (
    <div style={styles.chatPanel}>
      <div style={styles.chatHeader}>
        <div>
          <h2 style={styles.panelTitle}>Rollback Applied Changes</h2>
          <p style={styles.panelSubtitle}>
            Undo what this session applied. Preview first — nothing is touched until you apply.
          </p>
        </div>
        <button
          style={{ ...styles.resetButton, ...(loading ? styles.disabledButton : {}) }}
          onClick={onPreview}
          disabled={loading}
        >
          <Undo2 size={16} />
          {loading ? "Working..." : "Preview Rollback"}
        </button>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      <RollbackReportView data={result} forceIds={forceIds} onToggleForce={onToggleForce} />

      {result && (result.reverted?.length > 0 || result.blocked?.length > 0) && (
        <div style={{ display: "flex", gap: "10px", marginTop: "14px" }}>
          <button
            style={{ ...styles.primaryButton, ...(loading ? styles.disabledPrimary : {}) }}
            onClick={() => onApply(forceIds)}
            disabled={loading}
          >
            <PlayCircle size={18} />
            {hasBlocked && forceIds.length > 0
              ? `Force Revert Selected (${forceIds.length}) + Safe Items`
              : "Apply Rollback (safe items only)"}
          </button>
        </div>
      )}
    </div>
  );
}

// Provisions real test users, exercises the applied app's records/UI
// actions/scheduled jobs through the same ACL checks a real request would
// hit (see ai_architect_test_runner.py), then cleans everything up and
// shows a pass/fail report — the "here's proof it works" deliverable, not
// just another build step. Only offered once a package has actually been
// applied (visible={currentStep === "apply"}), and only exercises business
// rules/UI actions that have already been approved via the Needs Review
// tab — anything still pending there simply isn't real yet to test.
function TestMyAppPanel({ visible, sessionId, uiMode }) {
  const [phase, setPhase] = useState("idle"); // idle | planning | plan_ready | running | report_ready
  const [plan, setPlan] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  if (!visible) return null;

  const post = async (path) => {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.detail || "Request failed.");
    return body;
  };

  const startPlanning = async () => {
    setError("");
    setPhase("planning");
    try {
      const data = await post(`/ai-architect/sessions/${sessionId}/test/plan`);
      setPlan(data.plan);
      setPhase("plan_ready");
    } catch (e) {
      setError(e.message || "Couldn't put together a test plan.");
      setPhase("idle");
    }
  };

  const runTest = async () => {
    setError("");
    setPhase("running");
    try {
      await post(`/ai-architect/sessions/${sessionId}/test/run`);
      const reportData = await post(`/ai-architect/sessions/${sessionId}/test/report`);
      setReport(reportData);
      setPhase("report_ready");
    } catch (e) {
      setError(e.message || "The test run hit a problem.");
      setPhase("plan_ready");
    }
  };

  const reset = () => {
    setPhase("idle");
    setPlan(null);
    setReport(null);
    setError("");
  };

  return (
    <div style={styles.chatPanel}>
      <div style={styles.chatHeader}>
        <div>
          <h2 style={styles.panelTitle}>
            <ClipboardCheck size={19} />
            Test my app
          </h2>
          <p style={styles.panelSubtitle}>
            {uiMode === "friendly"
              ? "I'll create a couple of test users, try out the real flow, and show you exactly what happened."
              : "Provisions real test users, exercises records/UI actions/scheduled jobs through the same ACL checks a real request would hit, then cleans everything up."}
          </p>
        </div>
        {phase === "idle" && (
          <button style={styles.resetButton} onClick={startPlanning}>
            <PlayCircle size={16} />
            Test my app
          </button>
        )}
      </div>

      {error && (
        <div style={styles.errorBanner}>
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {phase === "planning" && <SoftEmpty text="Putting together a test plan…" />}

      {phase === "plan_ready" && plan && (
        <>
          <p style={styles.testPlanSummary}>{plan.summary}</p>
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button style={styles.primaryButton} onClick={runTest}>
              <PlayCircle size={18} />
              Run this test
            </button>
            <button style={styles.resetButton} onClick={reset}>
              Cancel
            </button>
          </div>
        </>
      )}

      {phase === "running" && (
        <SoftEmpty text="Running the test — creating test users, trying things out…" />
      )}

      {phase === "report_ready" && report && (
        <TestReportView report={report} uiMode={uiMode} onReset={reset} />
      )}
    </div>
  );
}

function TestReportView({ report, uiMode, onReset }) {
  const total = report.summary?.total || 0;
  const passed = report.summary?.passed || 0;
  const allPassed = total > 0 && passed === total;

  return (
    <div>
      <div
        style={{
          ...styles.testReportBanner,
          ...(allPassed ? styles.testReportBannerOk : styles.testReportBannerWarn),
        }}
      >
        {allPassed ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
        <div>
          <strong>
            {passed} of {total} steps passed
          </strong>
          <p style={{ margin: "2px 0 0" }}>{report.plan_summary}</p>
        </div>
      </div>

      <div style={{ marginTop: "12px" }}>
        {(report.steps || []).map((step, index) => (
          <div key={index} style={styles.testStepRow}>
            <span style={{ color: step.passed ? "#16a34a" : "#dc2626", flexShrink: 0 }}>
              {step.passed ? <Check size={15} /> : <XCircle size={15} />}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.testStepDescription}>{step.description}</div>
              <div style={styles.testStepDetail}>
                {step.persona ? `Acting as ${step.persona} · ` : ""}
                {step.actual || step.error || ""}
              </div>
            </div>
          </div>
        ))}
      </div>

      {report.cleanup && (
        <p style={styles.testCleanupNote}>
          Cleaned up {report.cleanup.records_removed} test record
          {report.cleanup.records_removed === 1 ? "" : "s"}.
          {report.cleanup.errors?.length > 0
            ? ` ${report.cleanup.errors.length} couldn't be removed automatically.`
            : ""}
        </p>
      )}

      {uiMode === "technical" && (
        <InfoBlock title="Raw Report">
          <JsonBox data={report} />
        </InfoBlock>
      )}

      <button style={{ ...styles.resetButton, marginTop: "12px" }} onClick={onReset}>
        <RotateCcw size={16} />
        Test again
      </button>
    </div>
  );
}

function RawTab({ data }) {
  return (
    <InfoBlock title="Latest Raw Response">
      <JsonBox data={data} />
    </InfoBlock>
  );
}

function QuestionList({ questions }) {
  return (
    <ol style={styles.questionList}>
      {questions.map((q, index) => (
        <li key={index}>
          {typeof q === "string" ? q : q.question || q.message || JSON.stringify(q)}
        </li>
      ))}
    </ol>
  );
}

function WarningList({ warnings }) {
  return (
    <ul style={styles.warningList}>
      {warnings.map((w, index) => (
        <li key={index}>
          {typeof w === "string" ? w : w.message || w.reason || JSON.stringify(w)}
        </li>
      ))}
    </ul>
  );
}

function InfoBlock({ title, children }) {
  return (
    <div style={styles.infoBlock}>
      <div style={styles.infoBlockTitle}>{title}</div>
      {children}
    </div>
  );
}

function SummaryGrid({ summary }) {
  return (
    <div style={styles.summaryGrid}>
      {Object.entries(summary || {}).map(([key, value]) => (
        <div key={key} style={styles.summaryCard}>
          <div style={styles.summaryValue}>{String(value)}</div>
          <div style={styles.summaryLabel}>{key.replaceAll("_", " ")}</div>
        </div>
      ))}
    </div>
  );
}

function JsonBox({ data }) {
  return <pre style={styles.smallPre}>{safeJson(data)}</pre>;
}

function SoftEmpty({ text }) {
  return (
    <div style={styles.softEmpty}>
      <CircleDashed size={24} />
      <p>{text}</p>
    </div>
  );
}

function ActionButton({ icon, title, disabled, onClick }) {
  return (
    <button
      style={{
        ...styles.miniActionButton,
        ...(disabled ? styles.disabledButton : {}),
      }}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
      {title}
    </button>
  );
}

function MessageCard({ message }) {
  const isUser = message.role === "user";
  const isError = message.type === "error";
  const isClarification = message.type === "clarification";
  const isWarning = message.type === "warning";
  const isSuccess = message.type === "success";

  return (
    <div
      style={{
        ...styles.messageRow,
        ...(isUser ? styles.messageRowUser : {}),
      }}
    >
      <div
        style={{
          ...styles.avatar,
          ...(isUser ? styles.avatarUser : styles.avatarAi),
          ...(isError ? styles.avatarError : {}),
          ...(isClarification ? styles.avatarClarification : {}),
        }}
      >
        {isUser ? <User size={17} /> : isError ? <XCircle size={17} /> : isClarification ? <HelpCircle size={17} /> : <Bot size={17} />}
      </div>

      <div
        style={{
          ...styles.messageCard,
          ...(isUser ? styles.userMessage : styles.aiMessage),
          ...(isError ? styles.errorMessage : {}),
          ...(isClarification ? styles.clarificationMessage : {}),
          ...(isWarning ? styles.warningMessage : {}),
          ...(isSuccess ? styles.successMessage : {}),
        }}
      >
        <div style={styles.messageMeta}>
          <span>{message.title}</span>
          <span>{message.createdAt}</span>
        </div>

        <div style={styles.messageText}>{message.content}</div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top left, rgba(59,130,246,.18), transparent 34%), radial-gradient(circle at top right, rgba(124,58,237,.16), transparent 28%), linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
    color: "#0f172a",
    padding: "32px",
  },
  shell: {
    maxWidth: "1680px",
    margin: "0 auto",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "20px",
    marginBottom: "22px",
  },
  heroActions: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  modeToggle: {
    display: "inline-flex",
    border: "1px solid var(--line, rgba(148,163,184,.45))",
    background: "rgba(255,255,255,.85)",
    borderRadius: "14px",
    padding: "4px",
    gap: "2px",
    boxShadow: "0 12px 30px rgba(15,23,42,.06)",
  },
  modeToggleBtn: {
    border: "none",
    background: "transparent",
    borderRadius: "10px",
    padding: "8px 13px",
    fontWeight: 800,
    fontSize: "13px",
    color: "#64748b",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  modeToggleBtnActive: {
    background: "var(--brand, #0d7d6f)",
    color: "#fff",
  },
  toggleLabel: {
    border: "1px solid rgba(148,163,184,.45)",
    background: "rgba(255,255,255,.85)",
    borderRadius: "14px",
    padding: "11px 14px",
    fontWeight: 800,
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 12px 30px rgba(15,23,42,.06)",
    fontSize: "13px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderRadius: "999px",
    background: "rgba(37,99,235,.1)",
    color: "#1d4ed8",
    fontWeight: 800,
    fontSize: "13px",
    marginBottom: "14px",
  },
  title: {
    margin: 0,
    fontSize: "46px",
    letterSpacing: "-0.05em",
    lineHeight: 1,
  },
  subtitle: {
    margin: "14px 0 0",
    maxWidth: "860px",
    color: "#475569",
    fontSize: "16px",
    lineHeight: 1.65,
  },
  resetButton: {
    border: "1px solid rgba(148,163,184,.45)",
    background: "rgba(255,255,255,.85)",
    borderRadius: "14px",
    padding: "11px 14px",
    fontWeight: 800,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 12px 30px rgba(15,23,42,.06)",
  },
  stepper: {
    display: "grid",
    gridTemplateColumns: "repeat(13, minmax(0, 1fr))",
    gap: "8px",
    marginBottom: "22px",
  },
  stepItem: {
    border: "1px solid rgba(148,163,184,.32)",
    background: "rgba(255,255,255,.66)",
    borderRadius: "16px",
    padding: "10px",
    display: "flex",
    gap: "8px",
    alignItems: "center",
    opacity: 0.72,
    minWidth: 0,
  },
  stepItemActive: {
    opacity: 1,
    border: "1px solid rgba(37,99,235,.42)",
    background: "white",
    boxShadow: "0 14px 35px rgba(37,99,235,.12)",
  },
  stepItemDone: {
    opacity: 1,
    background: "rgba(240,253,244,.92)",
    border: "1px solid rgba(34,197,94,.28)",
  },
  stepCircle: {
    width: "28px",
    height: "28px",
    borderRadius: "999px",
    background: "#eff6ff",
    color: "#2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    fontWeight: 900,
    flexShrink: 0,
  },
  stepCircleActive: {
    background: "#2563eb",
    color: "white",
  },
  stepCircleDone: {
    background: "#dcfce7",
    color: "#15803d",
  },
  stepTextWrap: {
    minWidth: 0,
  },
  stepLabel: {
    fontSize: "10px",
    fontWeight: 900,
    color: "#94a3b8",
  },
  stepTitle: {
    fontSize: "12px",
    fontWeight: 900,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  friendlyProgress: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "22px",
  },
  friendlyProgressItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    borderRadius: "999px",
    border: "1px solid rgba(148,163,184,.32)",
    background: "rgba(255,255,255,.66)",
    opacity: 0.62,
  },
  friendlyProgressItemActive: {
    opacity: 1,
    border: "1px solid rgba(37,99,235,.42)",
    background: "white",
    boxShadow: "0 14px 35px rgba(37,99,235,.12)",
  },
  friendlyProgressItemDone: {
    opacity: 1,
    background: "rgba(240,253,244,.92)",
    border: "1px solid rgba(34,197,94,.28)",
  },
  friendlyProgressDot: {
    width: "18px",
    height: "18px",
    borderRadius: "999px",
    background: "#eff6ff",
    color: "#2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  friendlyProgressDotActive: {
    background: "#2563eb",
    boxShadow: "0 0 0 4px rgba(37,99,235,.14)",
  },
  friendlyProgressDotDone: {
    background: "#dcfce7",
    color: "#15803d",
  },
  friendlyProgressLabel: {
    fontSize: "12.5px",
    fontWeight: 800,
    color: "#0f172a",
    whiteSpace: "nowrap",
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 540px",
    gap: "22px",
    alignItems: "start",
  },
  mainPanel: {
    minWidth: 0,
  },
  nextCard: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    background: "linear-gradient(135deg, #ffffff, #f8fafc)",
    border: "1px solid rgba(148,163,184,.32)",
    borderRadius: "24px",
    padding: "20px",
    boxShadow: "0 18px 45px rgba(15,23,42,.07)",
    marginBottom: "18px",
  },
  nextIcon: {
    width: "52px",
    height: "52px",
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #2563eb, #7c3aed)",
    color: "white",
    flexShrink: 0,
  },
  nextLabel: {
    fontSize: "12px",
    fontWeight: 900,
    color: "#2563eb",
    textTransform: "uppercase",
    letterSpacing: ".08em",
  },
  nextTitle: {
    margin: "4px 0 4px",
    fontSize: "22px",
    letterSpacing: "-0.03em",
  },
  nextDescription: {
    margin: 0,
    color: "#64748b",
    lineHeight: 1.5,
    fontSize: "14px",
  },
  primaryButton: {
    border: "none",
    borderRadius: "16px",
    padding: "13px 16px",
    background: "linear-gradient(135deg, #2563eb, #7c3aed)",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap",
  },
  disabledPrimary: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  errorBanner: {
    marginBottom: "16px",
    borderRadius: "16px",
    padding: "13px 14px",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: 700,
  },
  clarificationBanner: {
    marginBottom: "16px",
    borderRadius: "16px",
    padding: "14px",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    color: "#92400e",
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  chatPanel: {
    background: "rgba(255,255,255,.9)",
    border: "1px solid rgba(148,163,184,.3)",
    borderRadius: "24px",
    padding: "20px",
    boxShadow: "0 18px 45px rgba(15,23,42,.07)",
  },
  chatHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    alignItems: "center",
    marginBottom: "14px",
  },
  panelTitle: {
    margin: 0,
    fontSize: "18px",
    letterSpacing: "-0.02em",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  panelSubtitle: {
    margin: "5px 0 0",
    fontSize: "13px",
    color: "#64748b",
  },
  sessionPill: {
    borderRadius: "999px",
    padding: "8px 11px",
    background: "#ecfdf5",
    color: "#047857",
    fontSize: "12px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  chatBox: {
    minHeight: "540px",
    maxHeight: "720px",
    overflow: "auto",
    borderRadius: "20px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: "18px",
  },
  emptyState: {
    minHeight: "390px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#64748b",
  },
  messageRow: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
    marginBottom: "14px",
  },
  messageRowUser: {
    flexDirection: "row-reverse",
  },
  avatar: {
    width: "34px",
    height: "34px",
    borderRadius: "999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: "4px",
  },
  avatarAi: {
    background: "linear-gradient(135deg, #2563eb, #7c3aed)",
    color: "white",
  },
  avatarUser: {
    background: "#0f172a",
    color: "white",
  },
  avatarError: {
    background: "#dc2626",
    color: "white",
  },
  avatarClarification: {
    background: "#f59e0b",
    color: "white",
  },
  messageCard: {
    borderRadius: "18px",
    padding: "16px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 25px rgba(15,23,42,.05)",
  },
  userMessage: {
    maxWidth: "76%",
    background: "#dbeafe",
    borderColor: "#bfdbfe",
  },
  aiMessage: {
    maxWidth: "90%",
    background: "white",
  },
  errorMessage: {
    background: "#fef2f2",
    borderColor: "#fecaca",
  },
  clarificationMessage: {
    background: "#fffbeb",
    borderColor: "#fde68a",
  },
  warningMessage: {
    background: "#fff7ed",
    borderColor: "#fed7aa",
  },
  successMessage: {
    background: "#f0fdf4",
    borderColor: "#bbf7d0",
  },
  messageMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    color: "#2563eb",
    fontSize: "11px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    marginBottom: "10px",
  },
  messageText: {
    whiteSpace: "pre-wrap",
    color: "#1e293b",
    lineHeight: 1.65,
    fontSize: "14px",
  },
  loadingCard: {
    display: "inline-flex",
    alignItems: "center",
    gap: "9px",
    padding: "12px 14px",
    borderRadius: "999px",
    background: "white",
    border: "1px solid #e2e8f0",
    color: "#2563eb",
    fontWeight: 800,
    fontSize: "13px",
  },
  inputArea: {
    display: "flex",
    gap: "12px",
    marginTop: "14px",
    alignItems: "stretch",
  },
  chatInput: {
    flex: 1,
    minHeight: "86px",
    borderRadius: "18px",
    border: "1px solid #cbd5e1",
    padding: "14px",
    resize: "vertical",
    outline: "none",
    fontSize: "14px",
    lineHeight: 1.45,
    background: "white",
  },
  sendButton: {
    minWidth: "120px",
    border: "none",
    borderRadius: "18px",
    padding: "13px 16px",
    background: "#0f172a",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
  },
  sidePanel: {
    position: "sticky",
    top: "24px",
    background: "rgba(255,255,255,.92)",
    border: "1px solid rgba(148,163,184,.3)",
    borderRadius: "24px",
    padding: "18px",
    boxShadow: "0 18px 45px rgba(15,23,42,.07)",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    maxHeight: "calc(100vh - 48px)",
    overflow: "auto",
  },
  workspaceHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "flex-start",
  },
  sideHeader: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    fontWeight: 950,
    fontSize: "16px",
  },
  workspaceSubtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  quickActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
  },
  miniActionButton: {
    border: "1px solid #e2e8f0",
    background: "white",
    borderRadius: "14px",
    padding: "10px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "7px",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: 900,
  },
  applyButton: {
    border: "none",
    borderRadius: "14px",
    padding: "10px",
    background: "linear-gradient(135deg, #dc2626, #f97316)",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "7px",
    fontSize: "12px",
  },
  disabledButton: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  workspaceTabs: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "6px",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "6px",
  },
  workspaceTab: {
    border: "none",
    background: "transparent",
    borderRadius: "11px",
    padding: "8px 6px",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: 900,
    color: "#64748b",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "5px",
  },
  workspaceTabActive: {
    background: "white",
    color: "#2563eb",
    boxShadow: "0 8px 20px rgba(15,23,42,.08)",
  },
  workspaceBody: {
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    borderRadius: "18px",
    padding: "12px",
  },
  workspaceEmpty: {
    minHeight: "260px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    color: "#64748b",
    padding: "20px",
  },
  infoBlock: {
    background: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "12px",
    marginBottom: "10px",
  },
  infoBlockTitle: {
    fontSize: "12px",
    fontWeight: 950,
    color: "#0f172a",
    marginBottom: "9px",
    textTransform: "uppercase",
    letterSpacing: ".06em",
  },
  statusCard: {
    borderRadius: "16px",
    padding: "13px",
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    marginBottom: "10px",
    border: "1px solid #e2e8f0",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  statusInfo: {
    background: "#eff6ff",
    borderColor: "#bfdbfe",
    color: "#1e3a8a",
  },
  statusSuccess: {
    background: "#f0fdf4",
    borderColor: "#bbf7d0",
    color: "#166534",
  },
  statusWarning: {
    background: "#fff7ed",
    borderColor: "#fed7aa",
    color: "#9a3412",
  },
  statusClarification: {
    background: "#fffbeb",
    borderColor: "#fde68a",
    color: "#92400e",
  },
  statusError: {
    background: "#fef2f2",
    borderColor: "#fecaca",
    color: "#991b1b",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))",
    gap: "10px",
  },
  summaryCard: {
    borderRadius: "14px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    padding: "12px",
  },
  summaryValue: {
    fontSize: "21px",
    fontWeight: 950,
    color: "#0f172a",
  },
  summaryLabel: {
    marginTop: "3px",
    fontSize: "11px",
    color: "#64748b",
    textTransform: "capitalize",
  },
  timeline: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  timelineItem: {
    display: "flex",
    gap: "10px",
    justifyContent: "space-between",
    fontSize: "12px",
    color: "#64748b",
  },
  smallPre: {
    margin: 0,
    maxHeight: "460px",
    overflow: "auto",
    fontSize: "11.5px",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    background: "#0f172a",
    color: "#e2e8f0",
    borderRadius: "12px",
    padding: "12px",
  },

  approvalCard: {
    border: "1px solid rgba(148,163,184,.32)",
    borderRadius: "14px",
    padding: "14px",
    marginBottom: "12px",
    background: "white",
  },
  approvalCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "10px",
  },
  approvalCardTitle: {
    fontSize: "13.5px",
    fontWeight: 900,
    color: "#0f172a",
  },
  approvalCardMeta: {
    fontSize: "11.5px",
    color: "#64748b",
    marginTop: "2px",
  },
  approvalStatusBadge: {
    fontSize: "10.5px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: ".04em",
    padding: "3px 9px",
    borderRadius: "999px",
    flexShrink: 0,
  },
  approvalStatusPending: {
    background: "rgba(245,158,11,.14)",
    color: "#92400e",
  },
  approvalStatusApproved: {
    background: "rgba(34,197,94,.14)",
    color: "#15803d",
  },
  approvalStatusRejected: {
    background: "rgba(239,68,68,.14)",
    color: "#b91c1c",
  },
  approvalCardActions: {
    display: "flex",
    gap: "8px",
    marginTop: "12px",
  },
  approveButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    border: "none",
    borderRadius: "10px",
    padding: "8px 14px",
    fontWeight: 800,
    fontSize: "12.5px",
    cursor: "pointer",
    background: "#16a34a",
    color: "white",
  },
  rejectButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    border: "1px solid rgba(239,68,68,.35)",
    borderRadius: "10px",
    padding: "8px 14px",
    fontWeight: 800,
    fontSize: "12.5px",
    cursor: "pointer",
    background: "white",
    color: "#b91c1c",
  },

  testPlanSummary: {
    fontSize: "13.5px",
    color: "#1e293b",
    lineHeight: 1.5,
    background: "#f8fafc",
    border: "1px solid rgba(148,163,184,.28)",
    borderRadius: "12px",
    padding: "12px 14px",
    margin: 0,
  },
  testReportBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    borderRadius: "12px",
    padding: "12px 14px",
    fontSize: "13px",
  },
  testReportBannerOk: {
    background: "rgba(34,197,94,.10)",
    color: "#15803d",
    border: "1px solid rgba(34,197,94,.25)",
  },
  testReportBannerWarn: {
    background: "rgba(245,158,11,.10)",
    color: "#92400e",
    border: "1px solid rgba(245,158,11,.25)",
  },
  testStepRow: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
    padding: "8px 4px",
    borderBottom: "1px solid rgba(148,163,184,.16)",
  },
  testStepDescription: {
    fontSize: "12.5px",
    fontWeight: 700,
    color: "#0f172a",
  },
  testStepDetail: {
    fontSize: "11.5px",
    color: "#64748b",
    marginTop: "2px",
  },
  testCleanupNote: {
    fontSize: "11.5px",
    color: "#64748b",
    marginTop: "10px",
  },

  mermaidBox: {
    margin: 0,
    maxHeight: "220px",
    overflow: "auto",
    fontSize: "11.5px",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    background: "#eff6ff",
    color: "#1e3a8a",
    borderRadius: "12px",
    padding: "12px",
    border: "1px solid #bfdbfe",
  },
  softEmpty: {
    minHeight: "220px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    color: "#64748b",
    padding: "18px",
  },
  safetyNote: {
    borderRadius: "16px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    padding: "13px",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  questionList: {
    margin: 0,
    paddingLeft: "20px",
    color: "#334155",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  warningList: {
    margin: 0,
    paddingLeft: "20px",
    color: "#9a3412",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  recentWrap: {
    marginTop: "26px",
    width: "100%",
    maxWidth: "520px",
    textAlign: "left",
  },
  recentHeader: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "12px",
    fontWeight: 800,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "10px",
  },
  recentEmpty: {
    fontSize: "13px",
    color: "#94a3b8",
  },
  recentItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    width: "100%",
    textAlign: "left",
    border: "1px solid #e2e8f0",
    background: "#fff",
    borderRadius: "12px",
    padding: "10px 12px",
    marginBottom: "8px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  recentItemText: {
    fontSize: "13px",
    color: "#334155",
    fontWeight: 600,
  },
  recentItemMeta: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    fontWeight: 800,
    color: "#2563eb",
    whiteSpace: "nowrap",
  },
};
